import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const handleCategoryChange = useCallback(
    (newSlug: string) => {
      setActiveSlug(newSlug);
      navigate(`/category/${newSlug}`);
      window.scrollTo({ top: 0, behavior: 'instant' });
    },
    [navigate],
  );

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '版块分类', href: '/category' },
            { label: activeCategory.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                版块分类
              </h1>
              <p className="text-sm text-foreground-500">选择你感兴趣的版块浏览内容</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mb-8 p-5 bg-primary-50 rounded-lg border border-primary-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${activeCategory.icon} text-lg text-primary-600`}></i>
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-1">
                    {activeCategory.name}
                  </h2>
                  <p className="text-sm text-foreground-500 mb-2">{activeCategory.description}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-400">
                    <span>帖子总数：{activeCategory.postCount.toLocaleString()}</span>
                    <span>今日新增：{activeCategory.todayCount}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该版块暂无内容</p>
                </div>
              ) : (
                filteredArticles.map((post) => (
                  <Link
                    key={post.id}
                    to={`/article/${post.id}`}
                    className="block p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100">
                        <span className="text-sm font-medium text-secondary-600">
                          {activeCategory.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {post.isPinned && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-600 rounded-full">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-pushpin-line text-[10px]"></i>
                              </div>
                              置顶
                            </span>
                          )}
                          {post.isHot && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-600 rounded-full">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-fire-line text-[10px]"></i>
                              </div>
                              热门
                            </span>
                          )}
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200">
                          {post.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mb-3 line-clamp-2 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-3">
                            <span>{post.author}</span>
                            <span>{post.publishDate}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-eye-line text-[10px]"></i>
                              </div>
                              {(post.views / 1000).toFixed(1)}k
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-chat-3-line text-[10px]"></i>
                              </div>
                              {post.replies}
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-heart-line text-[10px]"></i>
                              </div>
                              {post.likes}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}